import { BLUE, DANGER, GREEN } from '../../../Theme/palette';
import { CheckOutlined, DownloadOutlined, InboxOutlined, WarningOutlined } from '@ant-design/icons';
import { L } from '@lib';
import { ImportInventoryResult, ImportInventoryRow, InventoryService } from '@services';
import { Alert, Button, Drawer, Space, Table, Typography, message } from 'antd';
import { forwardRef, useImperativeHandle, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;

export interface InventoryImportDrawerHandle {
  open: () => void;
}

interface IProps {
  onSuccess: () => void;
}

// Column order mirrors the Stock Excel export so a round-trip (export → tweak → import) "just works".
const TEMPLATE_COLUMNS = [
  'Purchase Date',
  'Supplier',
  'Supplier Inv #',
  'Supplier Part #',
  'Nominal Code',
  'Garage Ref #',
  'Part Name',
  'Description',
  'Category',
  'Qty',
  'Regular item',
  'Min Stock',
  'Puchase £',
  'Retail £',
  'Part Location',
  'Umoto ID',
];

const EXAMPLE_ROW: Record<string, any> = {
  'Purchase Date': '15/04/2026',
  Supplier: 'Acme Parts',
  'Supplier Inv #': 'INV-2026-001',
  'Supplier Part #': 'OEM-99887',
  'Nominal Code': 'NM-BODY',
  'Garage Ref #': 'BP-12345',
  'Part Name': 'Brake pad set',
  Description: 'Front brake pads, ceramic',
  Category: 'Brakes',
  Qty: 10,
  'Regular item': 'Yes',
  'Min Stock': 2,
  'Puchase £': 20.0,
  'Retail £': 40.0,
  'Part Location': 'A3-12',
  'Umoto ID': '',
};

const downloadTemplate = () => {
  const sheet = XLSX.utils.json_to_sheet([EXAMPLE_ROW], { header: TEMPLATE_COLUMNS });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Inventory');
  XLSX.writeFile(workbook, 'inventory-import-template.xlsx');
};

const parseNumber = (val: any): number => {
  if (val === null || val === undefined || val === '') return 0;
  const n = typeof val === 'number' ? val : Number(String(val).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const parseString = (val: any): string | undefined => {
  if (val === null || val === undefined) return undefined;
  const s = String(val).trim();
  return s.length > 0 ? s : undefined;
};

const parseDate = (val: any): string | undefined => {
  if (val === null || val === undefined || val === '') return undefined;
  // Excel serial number for dates → JS Date
  if (typeof val === 'number') {
    const d = XLSX.SSF.parse_date_code(val);
    if (d) return new Date(Date.UTC(d.y, d.m - 1, d.d)).toISOString();
  }
  const raw = String(val).trim();
  // Try UK numeric (DD/MM/YYYY) first since the export emits that format.
  const ukFormats = ['DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY', 'DD MMM YYYY', 'YYYY-MM-DD'];
  for (const fmt of ukFormats) {
    const d = dayjs(raw, fmt, true);
    if (d.isValid()) return d.toISOString();
  }
  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? undefined : fallback.toISOString();
};

const parseBool = (val: any): boolean | undefined => {
  if (val === null || val === undefined || val === '') return undefined;
  const s = String(val).trim().toLowerCase();
  if (s === 'yes' || s === 'y' || s === 'true' || s === '1') return true;
  if (s === 'no' || s === 'n' || s === 'false' || s === '0') return false;
  return undefined;
};

const rowsFromSheet = (file: File): Promise<ImportInventoryRow[]> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        // Read by current export column names; fall back to legacy names for backwards compat with old templates.
        const rows: ImportInventoryRow[] = json.map((r) => ({
          id: parseString(r['Umoto ID']) ?? parseString(r.Id),
          name: parseString(r['Part Name']) ?? parseString(r.Name) ?? '',
          reference: parseString(r['Garage Ref #']) ?? parseString(r.Reference),
          partNumber: parseString(r['Supplier Part #']) ?? parseString(r['Part #']),
          nominalCode: parseString(r['Nominal Code']),
          purchaseDate: parseDate(r['Purchase Date']),
          description: parseString(r.Description),
          categoryName: parseString(r.Category),
          supplierName: parseString(r.Supplier),
          supplierEmail: parseString(r['Supplier Email']),
          supplierPhone: parseString(r['Supplier Phone']),
          supplierAddress: parseString(r['Supplier Address']),
          supplierInvoiceNumber: parseString(r['Supplier Inv #']) ?? parseString(r['Invoice #']),
          quantity: parseNumber(r.Qty),
          minStockLevel: parseNumber(r['Min Stock']),
          costPrice: parseNumber(r['Puchase £']) || parseNumber(r['Purchase £']) || parseNumber(r.Cost),
          sellPrice: parseNumber(r['Retail £']) || parseNumber(r.Sale),
          isInShop: parseBool(r['Regular item']),
          location: parseString(r['Part Location']) ?? parseString(r.Location),
        }));
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

export const InventoryImportDrawer = forwardRef<InventoryImportDrawerHandle, IProps>(
  ({ onSuccess }, ref) => {
    const [open, setOpen] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [pendingRows, setPendingRows] = useState<ImportInventoryRow[] | null>(null);
    const [result, setResult] = useState<ImportInventoryResult | null>(null);

    const resetAll = () => {
      setPendingRows(null);
      setResult(null);
      setParsing(false);
      setImporting(false);
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        resetAll();
        setOpen(true);
      },
    }));

    const handleClose = () => {
      setOpen(false);
      resetAll();
    };

    const handleFile = async (file: File) => {
      setParsing(true);
      setResult(null);
      try {
        const rows = await rowsFromSheet(file);
        if (rows.length === 0) {
          message.warning(L('No rows found in the file.'));
          setPendingRows(null);
          return;
        }
        setPendingRows(rows);
      } catch (err) {
        console.error(err);
        message.error(L('Failed to parse the file.'));
        setPendingRows(null);
      } finally {
        setParsing(false);
      }
    };

    const handleImport = async () => {
      if (!pendingRows) return;
      setImporting(true);
      try {
        const response = await InventoryService.import(pendingRows);
        setResult(response);
        setPendingRows(null);
        if (response.failed.length === 0) {
          message.success(
            L('Import complete: {0} created, {1} updated')
              .replace('{0}', String(response.created))
              .replace('{1}', String(response.updated))
          );
        } else {
          message.warning(
            L('Import partially complete: {0} rows failed')
              .replace('{0}', String(response.failed.length))
          );
        }
        onSuccess();
      } catch (err) {
        console.error(err);
        message.error(L('Failed to import.'));
      } finally {
        setImporting(false);
      }
    };

    const pickFile = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = (e: any) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
      };
      input.click();
    };

    const invalidRows = (pendingRows || []).reduce(
      (acc, r) => acc + (r.name && r.name.trim().length > 0 ? 0 : 1),
      0
    );

    return (
      <Drawer
        title={L('Import inventory from Excel')}
        size={700}
        open={open}
        onClose={handleClose}
        footer={
          <Space style={{ float: 'right' }}>
            {pendingRows && (
              <Button onClick={resetAll} disabled={importing}>
                {L('Discard')}
              </Button>
            )}
            <Button onClick={handleClose}>{L('Close')}</Button>
            {pendingRows && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={importing}
                onClick={handleImport}
              >
                {L('Import {0} rows').replace('{0}', String(pendingRows.length))}
              </Button>
            )}
          </Space>
        }
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          {!pendingRows && !result && (
            <>
              <Alert
                type="info"
                showIcon
                message={L('How it works')}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li>{L('Download the template below, or export current inventory to use as the starting point.')}</li>
                    <li>{L('Quantity in the file is treated as the final value — differences are recorded as stock adjustments.')}</li>
                    <li>{L('Rows match existing inventory by Id, or by Reference + Supplier when Id is empty.')}</li>
                    <li>{L('New suppliers are created automatically from Supplier / Email / Phone / Address columns.')}</li>
                  </ul>
                }
              />

              <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
                {L('Download template')}
              </Button>

              <div
                onClick={pickFile}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleFile(file);
                }}
                style={{
                  border: '2px dashed #d9d9d9',
                  borderRadius: 8,
                  padding: '24px 0',
                  textAlign: 'center',
                  cursor: parsing ? 'wait' : 'pointer',
                  background: '#fafafa',
                  opacity: parsing ? 0.6 : 1,
                }}
              >
                <p style={{ marginBottom: 4 }}>
                  <InboxOutlined style={{ fontSize: 28, color: '#13c2c2' }} />
                </p>
                <p style={{ margin: 0, fontSize: 14 }}>
                  {parsing ? L('Parsing...') : L('Click or drag Excel/CSV file to upload')}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8c8c8c' }}>
                  {L('.xlsx, .xls, .csv')}
                </p>
              </div>
            </>
          )}

          {pendingRows && (
            <>
              <Title level={5} style={{ margin: '0' }}>
                {L('Review {0} rows before importing').replace('{0}', String(pendingRows.length))}
              </Title>
              {invalidRows > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={L('{0} rows are missing a Name and will fail on import.').replace(
                    '{0}',
                    String(invalidRows)
                  )}
                />
              )}
              <Table
                size="small"
                scroll={{ y: 420 }}
                pagination={false}
                dataSource={pendingRows.map((r, idx) => ({ key: idx, ...r }))}
                columns={[
                  { title: '#', width: 40, render: (_: any, __: any, idx: number) => idx + 1 },
                  {
                    title: L('Name'),
                    dataIndex: 'name',
                    ellipsis: true,
                    render: (v: string) =>
                      v && v.trim() ? v : <span style={{ color: DANGER.tag }}>—</span>,
                  },
                  { title: L('Ref'), dataIndex: 'reference', width: 100, ellipsis: true },
                  { title: L('Supplier'), dataIndex: 'supplierName', ellipsis: true },
                  { title: L('Qty'), dataIndex: 'quantity', width: 60, align: 'right' as const },
                  {
                    title: L('Cost'),
                    dataIndex: 'costPrice',
                    width: 70,
                    align: 'right' as const,
                    render: (v: number) => (v ?? 0).toFixed(2),
                  },
                  {
                    title: L('Sale'),
                    dataIndex: 'sellPrice',
                    width: 70,
                    align: 'right' as const,
                    render: (v: number) => (v ?? 0).toFixed(2),
                  },
                ]}
              />
            </>
          )}

          {result && (
            <>
              <Title level={5} style={{ margin: '12px 0 0' }}>{L('Result')}</Title>
              <Space size={16} wrap>
                <Text>
                  <strong style={{ color: GREEN.badge }}>{result.created}</strong> {L('created')}
                </Text>
                <Text>
                  <strong style={{ color: BLUE.badge }}>{result.updated}</strong> {L('updated')}
                </Text>
                <Text>
                  <strong style={{ color: DANGER.tag }}>{result.failed.length}</strong> {L('failed')}
                </Text>
              </Space>

              {result.failed.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  message={L('Rows that were not saved')}
                  description={
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={result.failed.map((f, idx) => ({ key: idx, ...f }))}
                      columns={[
                        { title: L('Row'), dataIndex: 'rowIndex', width: 60, render: (i: number) => i + 2 },
                        { title: L('Name'), render: (_: any, r: any) => r.row?.name || '-' },
                        { title: L('Reference'), render: (_: any, r: any) => r.row?.reference || '-' },
                        { title: L('Reason'), dataIndex: 'error', ellipsis: true },
                      ]}
                    />
                  }
                />
              )}
            </>
          )}
        </Space>
      </Drawer>
    );
  }
);
